<template>
	<div>
		<v-breadcrumbs
			:items="breadCrumbItems"
		>
			<template #divider>
				<v-icon icon="$chevronRight" />
			</template>
		</v-breadcrumbs>
		<h1 v-if="languages">
			{{ subject }}
		</h1>
		<v-card>
			<v-card-title>
				<v-icon
					start
					icon="$emailEdit"
				/>
				<translate>Edit Email Template</translate>
			</v-card-title>
			<v-card-text>
				<div v-if="domains">
					<v-row>
						<v-col
							cols="6"
						>
							<tooltip-field
								v-model="emailTemplate.subject"
								:label="$gettext('Subject')"
								:tooltip-text="$gettext('Smarty syntax is possible')"
								required
								variant="outlined"
							/>
						</v-col>
						<v-col
							cols="6"
						>
							<h5>
								<translate>Available Smarty Variables</translate>
							</h5>
							<code
								v-for="variable in variables.concat(manualVariables)"
								:key="variable"
								style="color: purple"
							>{{ '${' + variable + '}' }}</code>
						</v-col>
					</v-row>
					<v-row>
						<v-col
							cols="6"
						>
							<v-select
								v-model="emailTemplate.languageId"
								:label="$gettext('Language')"
								:items="languages"
								item-value="id"
								item-title="name"
								variant="outlined"
							/>
						</v-col>
						<v-col
							cols="6"
						>
							<v-select
								v-model="emailTemplate.domainId"
								:label="$gettext('Domain')"
								:items="domains"
								item-value="id"
								item-title="name"
								variant="outlined"
							/>
						</v-col>
					</v-row>
				</div>
				<div>
					<hr>
					<h4>HTML</h4>
					<hr>
					<textarea
						id="body"
						required
					/>
					<hr>
				</div>
			</v-card-text>
			<v-card-actions v-if="emailTemplateType">
				<v-spacer />
				<dialog-button
					v-if="variables.concat(manualVariables).length > 0"
					:label="$gettext('Preview')"
					color="primary"
					:dialog-width="500"
				>
					<template #title>
						<h3>
							<translate>Preview Email Template</translate>
						</h3>
					</template>
					<template #default>
						<v-row
							v-if="variables.includes('order')"
						>
							<v-col>
								<v-text-field
									v-model="preOrderKey"
									:label="$gettext('Orderkey (not ID)')"
									required
									variant="outlined"
								/>
							</v-col>
						</v-row>
						<v-row
							v-for="variable in variables"
							:key="variable"
						>
							<v-col
								v-if="!(variable === 'order')"
							>
								<v-text-field
									v-model="preVarIds[(variable+'_id')]"
									:label="$gettext(variable+' ID')"
									required
								/>
							</v-col>
						</v-row>
						<v-row
							v-for="variable in manualVariables"
							:key="variable"
						>
							<v-col>
								<v-textarea
									v-model="preManualVars[(variable)]"
									:label="$gettext(variable)"
									rows="4"
									auto-grow
									required
									variant="outlined"
								/>
							</v-col>
						</v-row>
					</template>
					<template #actions>
						<v-btn
							color="primary"
							variant="text"
							@click="showPreview"
						>
							<translate>Preview</translate>
						</v-btn>
					</template>
				</dialog-button>
				<v-btn
					v-else
					color="primary"
					@click="showPreview"
				>
					<translate>Preview</translate>
				</v-btn>


				<v-btn
					color="error"
					variant="flat"
					@click="deleteTemplate"
				>
					<translate>Delete</translate>
				</v-btn>
				<v-btn
					color="primary"
					variant="flat"
					@click="saveTemplate"
				>
					<translate>Save</translate>
				</v-btn>
			</v-card-actions>
		</v-card>
	</div>
</template>


<script>
import {
	axios,
} from '../../../../services/axios-service';
import {
	confirm,
	displayError,
} from 'laravel-front-library';
import * as ace from 'ace-builds';
import 'ace-builds/src-noconflict/mode-smarty';
import 'ace-builds/src-noconflict/theme-tomorrow';
import DialogButton from '../../../molecules/DialogButton.vue';
import TooltipField from '../../../atoms/TooltipField.vue';

export default {
	name: 'EmailTemplateEdit',
	components: {
		TooltipField,
		DialogButton
	},
	data () {
		return {
			subject: '',
			emailTemplateId: null,
			emailTemplate: null,
			emailTemplateType: null,
			variables: [],
			manualVariables: [],
			breadCrumbItems: [],
			editor: null,
			languages: null,
			domains: null,
			previewWindow: false,
			preOrderKey: '',
			preVarIds: {
			},
			preManualVars: {
			},
			previewParams: ''
		};
	},

	mounted () {
		const uri = window.location.search.substring(1);
		const params = new URLSearchParams(uri);

		this.getLanguages();
		this.emailTemplateId = params.get('id');
		if (this.emailTemplateId) {
			this.getData();
		}
	},

	methods: {
		/**
		 * Retrieve all data for the email template
		 *
		 * @returns {void}
		 */
		getData () {
			axios.get(`editdata?id=${this.emailTemplateId}`)
				.then(({
					data: data
				}) => {
					this.initialiseData(data);
				}).catch(() => {
					displayError(this.$gettext('Something has gone wrong during data retrieval'));
				});
		},

		/**
		 * Retrieve list of supported languages
		 *
		 * @returns {void}
		 */
		getLanguages () {
			axios.get('/api/v0/languages/supported')
				.then(({
					data: languages
				}) => {
					this.languages = languages;
				}).catch((error) => {
					displayError(this.$gettext('Something has gone wrong during data retrieval'));
					window.captureException(error);
				});
		},

		/**
		 * Initialise all variables using retrieved data
		 *
		 * @param {string} data - data from get response
		 * @returns {void}
		 */
		initialiseData (data) {
			this.emailTemplate = data.emailTemplate;
			this.subject = this.emailTemplate.subject;

			this.emailTemplateType = data.emailTemplateType;
			this.variables = this.emailTemplateType.variables ? this.emailTemplateType.variables.split(',') : [];
			this.manualVariables = this.emailTemplateType.manualVariables ? this.emailTemplateType.manualVariables.split(',') : [];

			this.domains = data.domains;

			this.breadCrumbItems = [
				{
					title: this.$gettext('Email Templates'),
					disabled: false,
					href: '/bo/emailtemplates'
				},
				{
					title: this.$gettext('View Email Template'),
					disabled: false,
					href: `view?id=${this.emailTemplateType.id}`
				},
				{
					title: this.emailTemplate.subject,
					disabled: true,
				},
			];

			this.editor = ace.edit('body');
			this.editor.setTheme('ace/theme/tomorrow');
			this.editor.getSession().setMode('ace/mode/smarty');
			this.editor.resize();
			this.editor.setValue(this.emailTemplate.body, 1);
		},

		/**
		 * Show a preview of the email template in another tab, given some mock variables
		 *
		 * @returns {void}
		 */
		showPreview () {
			this.previewParams = '';
			this.previewParams += this.toParameter('id', this.emailTemplateId) + '&';
			for (const variable in this.preVarIds) {
				this.previewParams += this.toParameter(variable, this.preVarIds[variable]) + '&';
			}
			for (const variable in this.preManualVars) {
				this.previewParams += this.toParameter(variable, this.preManualVars[variable]) + '&';
			}
			this.previewParams += this.toParameter('order_id', this.preOrderKey);

			window.open('/bo/preview?' + this.previewParams);
		},

		/**
		 * Convert variable to a get parameter
		 *
		 * @param {string} variable - Variable name
		 * @param {number} value - Variable value
		 * @returns {string} Variable as a get parameter
		 */
		toParameter (variable, value) {
			return variable + '=' + value;
		},

		/**
		 * Attempt to delete this email template.
		 */
		deleteTemplate () {
			confirm(this.$gettext('Are you sure you want to delete this template?'), () => {
				axios.delete(`/bo/emailtemplates?id=${this.emailTemplateId}`).then(() => {
					window.location.href = `/bo/emailtemplates/view?id=${this.emailTemplateType.id}`;
				}).catch(() => {
					window.location.reload();
				});
			});
		},

		/**
		 * Save the changes made to the email template and reload the page
		 *
		 * @returns {void}
		 */
		saveTemplate () {
			const formData = new FormData();

			formData.append('id', this.emailTemplateId);
			formData.append('subject', this.emailTemplate.subject);
			formData.append('body', this.editor.getValue());
			formData.append('language', this.emailTemplate.languageId);
			formData.append('domain', this.emailTemplate.domainId);

			axios({
				url: 'edit',
				method: 'POST',
				dataType: 'application/json',
				data: formData,
				config: {
					headers: {
						'Content-Type': 'multipart/form-data'
					},
				},
			}).catch((error) => {
				displayError(error);
			}).finally(() => {
				window.location.reload();
			});
		},
	}
};
</script>
